import { useState, useRef, useEffect } from 'react';
import { Autocomplete, Box, CircularProgress, TextField, useTheme } from '@mui/material';
import { Map, Overlay, ZoomControl } from 'pigeon-maps';
import { NodeType } from '../../../types/node';
import { Point } from '../../../types/location';
import { PositionService } from '../../../http/position-service';

// prettier-ignore
export const NODE_TYPE_LABELS: Record<NodeType, string> = {
    [NodeType.Root]         : 'Корневой',
    [NodeType.Organization] : 'Организация',
    [NodeType.Department]   : 'Подразделение',
    [NodeType.Group]        : 'Группа',
    [NodeType.Borehole]     : 'Скважина',
};

export const SELECTABLE_TYPES = [NodeType.Organization, NodeType.Department, NodeType.Group, NodeType.Borehole];

interface DadataSuggestion {
    value: string;
    data: { geo_lat: string | null; geo_lon: string | null };
}

function darkTiles(x: number, y: number, z: number, dpr?: number): string {
    const r = dpr && dpr >= 2 ? '@2x' : '';
    const s = ['a', 'b', 'c', 'd'][(x + y) % 4];
    return `https://${s}.basemaps.cartocdn.com/dark_all/${z}/${x}/${y}${r}.png`;
}

const DEFAULT_CENTER: [number, number] = [55.75, 37.62];
const DEFAULT_ZOOM = 10;

interface NodeLocationFormProps {
    address: string;
    coordinates: Point | null;
    /** Increment to snap the map to current coordinates (e.g. after loading existing location). */
    centerTrigger?: number;
    onChange: (address: string, coordinates: Point | null) => void;
    onDirty?: () => void;
}

const NodeLocationForm = ({ address, coordinates, centerTrigger, onChange, onDirty }: NodeLocationFormProps) => {
    const theme = useTheme();
    const [suggestions, setSuggestions] = useState<DadataSuggestion[]>([]);
    const [hintsLoading, setHintsLoading] = useState(false);
    const [mapKey, setMapKey] = useState(0);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (centerTrigger) setMapKey((k) => k + 1);
    }, [centerTrigger]);

    const handleAddressInput = (_: unknown, value: string) => {
        onDirty?.();
        onChange(value, coordinates);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!value.trim()) {
            setSuggestions([]);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            setHintsLoading(true);
            try {
                const answer = await PositionService.addressHint(value);
                setSuggestions(answer.data as DadataSuggestion[]);
            } catch {
                setSuggestions([]);
            } finally {
                setHintsLoading(false);
            }
        }, 350);
    };

    const handleSuggestionSelect = (_: unknown, suggestion: DadataSuggestion | string | null) => {
        if (!suggestion || typeof suggestion === 'string') return;
        onDirty?.();
        const newCoords =
            suggestion.data.geo_lat && suggestion.data.geo_lon
                ? { x: parseFloat(suggestion.data.geo_lat), y: parseFloat(suggestion.data.geo_lon) }
                : coordinates;
        onChange(suggestion.value, newCoords);
        if (newCoords !== coordinates) setMapKey((k) => k + 1);
        setSuggestions([]);
    };

    const handleMapClick = async ({ latLng }: { latLng: [number, number] }) => {
        const point: Point = { x: latLng[0], y: latLng[1] };
        onDirty?.();
        onChange(address, point);
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const answer = await PositionService.pointToAddress(point as any);
            const hints = answer.data as DadataSuggestion[];
            if (hints.length > 0) onChange(hints[0].value, point);
        } catch {
            // reverse geocoding is best-effort
        }
    };

    const mapCenter: [number, number] = coordinates ? [coordinates.x, coordinates.y] : DEFAULT_CENTER;
    const mapZoom = coordinates ? 14 : DEFAULT_ZOOM;

    return (
        <>
            <Autocomplete<DadataSuggestion, false, false, true>
                freeSolo
                options={suggestions}
                getOptionLabel={(o) => (typeof o === 'string' ? o : o.value)}
                loading={hintsLoading}
                inputValue={address}
                onInputChange={handleAddressInput}
                onChange={handleSuggestionSelect}
                filterOptions={(x) => x}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Адрес (необязательно)"
                        size="small"
                        fullWidth
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {hintsLoading && <CircularProgress size={16} />}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
            <Box
                sx={{
                    width: '100%',
                    height: 280,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                    cursor: 'crosshair',
                    ...(theme.palette.mode === 'dark' && {
                        '& .pigeon-tiles': {
                            filter: 'sepia(0.8) hue-rotate(215deg) saturate(3) brightness(1.5)',
                        },
                    }),
                }}
            >
                <Map
                    key={mapKey}
                    animate
                    defaultCenter={mapCenter}
                    defaultZoom={mapZoom}
                    provider={theme.palette.mode === 'dark' ? darkTiles : undefined}
                    onClick={handleMapClick}
                >
                    <ZoomControl style={{ right: '10px', top: '10px', left: 'auto' }} />
                    {coordinates && (
                        <Overlay anchor={[coordinates.x, coordinates.y]}>
                            <Box sx={{ transform: 'translate(-50%, -100%)', lineHeight: 0 }}>
                                <svg width="20" height="28" viewBox="0 0 20 28">
                                    <circle cx="10" cy="10" r="10" fill={theme.palette.primary.main} />
                                    <polygon points="5,18 15,18 10,28" fill={theme.palette.primary.main} />
                                </svg>
                            </Box>
                        </Overlay>
                    )}
                </Map>
            </Box>
        </>
    );
};

export default NodeLocationForm;
